import { useState } from "react";
import { getToken } from "../../services/tokenGET";

/**
 * Custom hook for managing species images
 * Handles fetching, uploading, and deleting species images
 * 
 * @param {number} selectedSpeciesId - Currently selected species ID
 * @returns {Object} Image data and handlers
 */
export function useSpeciesImages(selectedSpeciesId) {
    const [specieImageData, setSpeciesImageData] = useState([]);
    const [specieImages, setSpeciesImages] = useState([]);
    const [imageFiles, setImageFiles] = useState([1]);
    const [isAddImageModalOpen, setAddImageModalOpen] = useState(false);

    const retrieveSpecieImages = async (specieId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${specieId}/images`);
            
            if (!response.ok) {
                return [];
            }
    
            const data = await response.json();
            const images = data.images || [];
            
            const imageData = images.map(image => {
                const base64Url = `data:image/jpeg;base64,${image.base64}`;
                return { 
                    url: base64Url, 
                    filename: image.file_path,
                    image_id: image.image_id,
                    species_id: image.species_id
                };
            });

            return imageData;
        } catch (error) {
            console.error("Error retrieving specie images:", error);
            return [];
        }
    };

    const loadSpeciesImages = async (speciesId) => {
        const images = await retrieveSpecieImages(speciesId);
        setSpeciesImageData(images);
        setSpeciesImages(images.map(img => img.url));
    };

    const openAddImageModal = () => {
        setImageFiles([1]);
        setAddImageModalOpen(true);
    };

    const closeAddImageModal = () => {
        setAddImageModalOpen(false);
        setImageFiles([1]);
    };

    const handleImageChange = (event, index) => {
        const files = event.target.files;
        if (files.length > 0) {
            const newImageFiles = [...imageFiles];
            newImageFiles[index] = files[0];
            setImageFiles(newImageFiles);
        }
    };
    
    const handleAddImageInput = () => {
        setImageFiles([...imageFiles, imageFiles.length + 1]);
    };
    
    const handleRemoveImageInput = (index) => {
        const newImageFiles = [...imageFiles];
        newImageFiles.splice(index, 1);
        setImageFiles(newImageFiles);
    };
    
    const handleAddSpeciePictures = async () => {
        if (!selectedSpeciesId) {
            alert("Du måste spara arten först innan du kan lägga till bilder.");
            closeAddImageModal();
            return;
        }

        if (imageFiles.length === 0 || !imageFiles.some(f => f instanceof File)) {
            console.error("No images selected");
            alert("Inga bilder valda");
            return;
        }

        const formData = new FormData();
        imageFiles.forEach((file) => {
            if (file) formData.append("image_paths[]", file);
        });

        try {
            const token = await getToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/species/${selectedSpeciesId}/images`, {
                method: "POST",
                credentials: "include",
                headers: {
                    'X-CSRF-TOKEN': token
                },
                body: formData,
            });

            if (!response.ok) {
                let errorMsg = "Kunde inte ladda upp bilder.";
                try {
                    const data = await response.json();
                    console.error("Failed to upload images:", data);
                    if (data.message) errorMsg += " " + data.message;
                } catch (e) {
                    console.error("Failed to upload images, no JSON response");
                }
                alert(errorMsg + " Notera att filnamn får inte innehålla specialtecken. (Å, Ä, Ö etc.) / Inga mellanslag i filnamnet.");
                closeAddImageModal();
                return;
            }
                
            alert("Bilder uppladdade!");
            closeAddImageModal();
            await loadSpeciesImages(selectedSpeciesId);
            
        } catch (error) {
            console.error("Upload failed:", error);
            alert("Ett fel uppstod vid uppladdning: " + error.message);
        }
    };

    const handleDeleteImage = async (imageData) => {
        if (!window.confirm("Är du säker på att du vill radera denna bild?")) {
            return;
        }

        try {
            const token = await getToken();
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/species/${imageData.species_id}/images/${imageData.image_id}`, 
                {
                    method: "DELETE",
                    credentials: "include",
                    headers: {
                        'X-CSRF-TOKEN': token
                    },
                }
            );
    
            if (!response.ok) {
                let errorMessage = "Failed to delete image";
                try {
                    const errorData = await response.json();
                    console.error("Delete error response:", errorData);
                    errorMessage = errorData.Error || errorData.message || errorMessage;
                } catch (e) {
                    console.error("Could not parse error response");
                }
                throw new Error(errorMessage);
            }
    
            setSpeciesImages((prevImages) => prevImages.filter((img) => img !== imageData.url));
            setSpeciesImageData((prevData) => prevData.filter((img) => img.image_id !== imageData.image_id));
            alert("Bild raderad!");
        } catch (error) {
            console.error("Error deleting image:", error);
            alert("Kunde inte radera bilden: " + error.message);
        }
    };

    const clearImages = () => {
        setSpeciesImages([]);
        setSpeciesImageData([]);
    };

    return {
        specieImageData,
        specieImages,
        imageFiles,
        setImageFiles,
        isAddImageModalOpen,
        loadSpeciesImages,
        openAddImageModal,
        closeAddImageModal,
        handleImageChange,
        handleAddImageInput,
        handleRemoveImageInput,
        handleAddSpeciePictures,
        handleDeleteImage,
        clearImages,
    };
}
